// ────────────────────────────────────────────────────────────────────────────────
// ArchiveViewer: visualizar un archivo solo con los datos del modelo `archive`

// Props: { archive: { id, filename?, extension?, path? } }
// Soporta vista previa PDF (react-pdf) e imágenes comunes; otros tipos permiten descarga.
// ────────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import { IonButton, IonButtons, IonIcon, IonItem, IonLabel, IonText } from "@ionic/react";
import api from "../utils/api";
import { SizeMe } from "react-sizeme";
import { addOutline, removeOutline } from "ionicons/icons";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";


interface ArchiveLike {
  id: string | number;
  filename?: string;
  extension?: string; // ej: 'pdf', 'png', 'jpg'
  path?: string;
}

interface ArchiveViewerProps {
  archive: ArchiveLike;
}

export const ArchiveViewer: React.FC<ArchiveViewerProps> = ({ archive }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1.0);
  const ext = (archive.extension || "").toLowerCase().replace(".","")

  const isPdf = ext === "pdf";
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);

  useEffect(() => {
    const fetchArchive = async () => {
      if (!archive?.id) {
        console.error("No archive ID provided");
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${api.baseURL}archive/${archive.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          console.error("Error fetching archive:", response.statusText);
          return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setFileUrl(url);
        return () => URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };
    fetchArchive();
  }, [archive?.id]);

  function onDocumentLoadSuccess({ numPages }: any) {
    setNumPages(numPages);
  }

  const incrementZoom = () => setZoom(+(zoom + 0.1).toFixed(12));
  const decrementZoom = () => setZoom(+(zoom - 0.1).toFixed(12));

  const handleDownload = () => {
    if (!fileUrl) return;
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = archive.filename || `archivo_${archive.id}.${archive.extension || ""}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <section
      style={{
        maxHeight: "calc(100vh - 104px)",
        overflow: "auto",
        background: "#bcbcbc",
      }}
    >
      {fileUrl ? (
        <>
          <SizeMe>
            {({ size }) => (
              <div className={"doc-viewer"}>
                <IonItem color="dark">
                  <IonLabel>
                    <h4>
                      {archive.filename || `Archivo ${archive.id}`}

                    </h4>
                  </IonLabel>
                  <IonButtons slot="end">
                    <IonButton onClick={handleDownload}>Descargar</IonButton>
                  </IonButtons>
                  <IonButtons slot="end" className="ion-margin-start">
                    <IonButton onClick={decrementZoom}>
                      <IonIcon icon={removeOutline}></IonIcon>
                    </IonButton>
                    <IonText>{zoom * 100}%</IonText>
                    <IonButton onClick={incrementZoom}>
                      <IonIcon icon={addOutline}></IonIcon>
                    </IonButton>
                  </IonButtons>
                </IonItem>

                {isPdf ? (
                  <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading="Cargando documento..."
                    noData="No se ha encontrado el documento"
                  >
                    {Array.from(new Array(numPages), (el, index) => (
                      <article
                        style={{ display: "flex", overflow: "auto" }}
                        key={index}
                        className="ion-margin"
                      >
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          width={size.width ? size.width : 1}
                          scale={zoom}
                        />
                      </article>
                    ))}
                  </Document>
                ) : isImage ? (
                  <div className="ion-margin" style={{ display: "flex", justifyContent: "center" }}>
                    <img
                      src={fileUrl}
                      alt={archive.filename || `archivo_${archive.id}`}
                      style={{
                        width: size.width ? size.width * zoom : undefined,
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </div>
                ) : (
                  <div className="ion-padding">
                    <p>
                      Vista previa no soportada para <strong>{archive.extension || "desconocido"}</strong>.
                    </p>
                    <IonButton onClick={handleDownload}>Descargar archivo</IonButton>
                  </div>
                )}
              </div>
            )}
          </SizeMe>
        </>
      ) : (
        <p>Ha ocurrido un problema con la carga del archivo</p>
      )}
    </section>
  );
};
